import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConsultationPricingItemDto } from '../../models/consultation-pricing-item.model';
import { SettingsService } from '../../services/settings.service';
import { BookingService } from '../../services/booking.service';
import { NotificationService } from '../../services/notification.service';
import { ConsultationPricingRequest } from '../../models/consultation-pricing-request.model';

interface ConsultationPricingModalData {
  appointmentId: string;
  patientName?: string;
}

@Component({
  selector: 'app-consultation-pricing-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './consultation-pricing.modal.html',
  styleUrls: ['./consultation-pricing.modal.scss'],
})
export class ConsultationPricingModal implements OnInit {
  isLoading = false;
  isGenerating = false;
  errorMessage: string | null = null;

  pricingItems: ConsultationPricingItemDto[] = [];
  selectedItemIds: string[] = [];
  paidAmount: string | number = '';

  constructor(
    public dialogRef: MatDialogRef<ConsultationPricingModal>,
    @Inject(MAT_DIALOG_DATA) public data: ConsultationPricingModalData,
    private settingsService: SettingsService,
    private bookingService: BookingService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.loadPricingItems();
  }

  get dialogTitle(): string {
    return this.data.patientName
      ? `Consultation Pricing for ${this.data.patientName}`
      : 'Consultation Pricing';
  }

  get selectedItems(): ConsultationPricingItemDto[] {
    return this.pricingItems.filter((item) => this.selectedItemIds.includes(item.id));
  }

  get selectedTotal(): number {
    return this.selectedItems.reduce((total, item) => total + item.cost, 0);
  }

  toggleSelection(itemId: string, checked: boolean): void {
    if (checked) {
      this.selectedItemIds = [...this.selectedItemIds, itemId];
      return;
    }

    this.selectedItemIds = this.selectedItemIds.filter((id) => id !== itemId);
  }

  isSelected(itemId: string): boolean {
    return this.selectedItemIds.includes(itemId);
  }

  generate(): void {
    if (this.selectedItemIds.length === 0) {
      this.errorMessage = 'Select at least one consultation item before generating.';
      return;
    }

    const paidAmount = this.normalizePaidAmount(this.paidAmount);

    if (paidAmount !== null && Number.isNaN(paidAmount)) {
      this.errorMessage = 'Enter a valid paid amount or leave the field empty.';
      return;
    }

    if (paidAmount !== null && paidAmount > this.selectedTotal) {
      this.errorMessage = 'Amount paid cannot be greater than the selected consultation total.';
      return;
    }

    const request: ConsultationPricingRequest = {
      appointmentId: this.data.appointmentId,
      pricingItemIds: this.selectedItemIds,
      paidAmount,
    };

    this.isGenerating = true;
    this.errorMessage = null;

    this.bookingService.generateConsultationPricingDocument(request).subscribe({
      next: (blob) => {
        const fileUrl = window.URL.createObjectURL(blob);
        window.open(fileUrl, '_blank');
        this.notificationService.success('Consultation pricing generated successfully.');
        this.dialogRef.close(true);
        this.isGenerating = false;
      },
      error: (error) => {
        this.errorMessage =
          'Failed to generate consultation pricing: ' +
          (error?.error?.message || error.message || 'Unknown error');
        this.notificationService.error(this.errorMessage);
        this.isGenerating = false;
      },
    });
  }

  closeIfAllowed(): void {
    if (!this.isGenerating) {
      this.cancel();
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private normalizePaidAmount(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsedAmount = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(parsedAmount) ? NaN : parsedAmount;
  }

  private loadPricingItems(): void {
    this.isLoading = true;

    this.settingsService.getConsultationPricingItems().subscribe({
      next: (items) => {
        this.pricingItems = items ?? [];
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage =
          'Failed to load consultation pricing items: ' +
          (error?.error?.message || error.message || 'Unknown error');
        this.notificationService.error(this.errorMessage);
        this.isLoading = false;
      },
    });
  }
}
